import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Alert,
  Snackbar,
  InputAdornment,
  Tooltip,
  TablePagination,
  Skeleton,
  LinearProgress,
  Tabs,
  Tab,
  Divider,
  Stack,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  TrendingUp,
  AttachMoney,
  CalendarToday,
  Business,
  Refresh,
  EmojiEvents,
  Visibility,
  Notes as NotesIcon,
  Save,
  Close,
  RequestQuote,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import crmService, { Opportunity, Account, Contact, Quotation } from '../services/crmService';
import { useAuth } from '../contexts/AuthContext';

const OpportunitiesPage: React.FC = () => {
  const { user } = useAuth();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState<Opportunity | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Detail dialog states
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [viewingOpportunity, setViewingOpportunity] = useState<Opportunity | null>(null);
  const [detailTab, setDetailTab] = useState(0);
  const [notes, setNotes] = useState<string>('');
  const [newNote, setNewNote] = useState<string>('');
  const [updatingStage, setUpdatingStage] = useState(false);
  const [editExpectedCloseDate, setEditExpectedCloseDate] = useState<string>('');
  const [editProbability, setEditProbability] = useState<number>(0);
  const [updatingFields, setUpdatingFields] = useState(false);
  const [opportunityQuotations, setOpportunityQuotations] = useState<Quotation[]>([]);
  const [loadingQuotations, setLoadingQuotations] = useState(false);

  const [formData, setFormData] = useState<Partial<Opportunity>>({
    name: '',
    account: '',
    stage: 'Prospect',
    amount: 0,
    probability: 10,
    expectedCloseDate: new Date().toISOString().split('T')[0],
    type: 'New Business',
    description: '',
    nextStep: '',
  });

  // Fetch opportunities and accounts
  const fetchData = async () => {
    try {
      setLoading(true);
      const [oppsResponse, accountsResponse] = await Promise.all([
        crmService.getOpportunities(),
        crmService.getAccounts()
      ]);

      const oppsData = Array.isArray(oppsResponse) ? oppsResponse :
                      oppsResponse.opportunities ? oppsResponse.opportunities :
                      oppsResponse.data ? oppsResponse.data : [];
      const accountsData = Array.isArray(accountsResponse) ? accountsResponse :
                          accountsResponse.accounts ? accountsResponse.accounts :
                          accountsResponse.data ? accountsResponse.data : [];

      setOpportunities(oppsData);
      setAccounts(accountsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setSnackbar({ open: true, message: 'Failed to fetch opportunities', severity: 'error' });
      setOpportunities([]);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle form changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'amount' || name === 'probability' ? Number(value) : value
    }));
  };

  // Update probability based on stage
  const handleStageChange = (stage: string) => {
    const stageProbability: Record<string, number> = {
      'Prospect': 10,
      'First Call': 20,
      'Demo': 40,
      'POC': 60,
      'RFI/RFQ': 70,
      'Negotiation': 80,
      'Closed Won': 100,
      'Closed Lost': 0,
    };
    setFormData(prev => ({
      ...prev,
      stage: stage as 'Prospect' | 'First Call' | 'Demo' | 'POC' | 'RFI/RFQ' | 'Negotiation' | 'Closed Won' | 'Closed Lost',
      probability: stageProbability[stage] || 20
    }));
  };

  // Handle dialog open/close
  const handleOpenDialog = (opportunity?: Opportunity) => {
    if (opportunity) {
      setEditingOpportunity(opportunity);
      setFormData({
        ...opportunity,
        account: typeof opportunity.account === 'object' && opportunity.account !== null
          ? opportunity.account._id
          : opportunity.account || '',
        expectedCloseDate: opportunity.expectedCloseDate ?
          new Date(opportunity.expectedCloseDate).toISOString().split('T')[0] :
          new Date().toISOString().split('T')[0]
      });
    } else {
      setEditingOpportunity(null);
      setFormData({
        name: '',
        account: '',
        stage: 'Prospect',
        amount: 0,
        probability: 10,
        expectedCloseDate: new Date().toISOString().split('T')[0],
        type: 'New Business',
        description: '',
        nextStep: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingOpportunity(null);
  };

  // Handle save (create/update)
  const handleSave = async () => {
    try {
      if (editingOpportunity) {
        await crmService.updateOpportunity(editingOpportunity._id!, formData);
        setSnackbar({ open: true, message: 'Opportunity updated successfully', severity: 'success' });
      } else {
        // Auto-assign to current user when creating new opportunity
        const opportunityData = {
          ...formData,
          assignedTo: user?.id
        };
        await crmService.createOpportunity(opportunityData as Omit<Opportunity, '_id'>);
        setSnackbar({ open: true, message: 'Opportunity created successfully', severity: 'success' });
      }
      handleCloseDialog();
      fetchData();
    } catch (error) {
      console.error('Error saving opportunity:', error);
      setSnackbar({ open: true, message: 'Failed to save opportunity', severity: 'error' });
    }
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this opportunity?')) {
      try {
        await crmService.deleteOpportunity(id);
        setSnackbar({ open: true, message: 'Opportunity deleted successfully', severity: 'success' });
        fetchData();
      } catch (error) {
        console.error('Error deleting opportunity:', error);
        setSnackbar({ open: true, message: 'Failed to delete opportunity', severity: 'error' });
      }
    }
  };

  // Handle view details
  const handleViewDetails = (opportunity: Opportunity) => {
    setViewingOpportunity(opportunity);
    setNotes(opportunity.notes || '');
    setNewNote('');
    setDetailTab(0);
    setOpportunityQuotations([]);
    setEditExpectedCloseDate(
      opportunity.expectedCloseDate
        ? new Date(opportunity.expectedCloseDate).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0]
    );
    setEditProbability(opportunity.probability);
    setOpenDetailDialog(true);
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setViewingOpportunity(null);
    setNotes('');
    setNewNote('');
    setDetailTab(0);
  };

  // Handle stage update
  const handleStageUpdate = async (newStage: string) => {
    if (!viewingOpportunity) return;

    try {
      setUpdatingStage(true);
      const stageValue = newStage as 'Prospect' | 'First Call' | 'Demo' | 'POC' | 'RFI/RFQ' | 'Negotiation' | 'Closed Won' | 'Closed Lost';
      await crmService.updateOpportunity(viewingOpportunity._id!, { stage: stageValue });
      setSnackbar({ open: true, message: 'Status updated successfully', severity: 'success' });
      fetchData();
      setViewingOpportunity({ ...viewingOpportunity, stage: stageValue });
    } catch (error) {
      console.error('Error updating stage:', error);
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    } finally {
      setUpdatingStage(false);
    }
  };

  // Handle expected close date and probability update
  const handleFieldsUpdate = async () => {
    if (!viewingOpportunity) return;

    try {
      setUpdatingFields(true);
      await crmService.updateOpportunity(viewingOpportunity._id!, {
        expectedCloseDate: editExpectedCloseDate,
        probability: editProbability,
      });
      setSnackbar({ open: true, message: 'Opportunity updated successfully', severity: 'success' });
      fetchData();
      setViewingOpportunity({
        ...viewingOpportunity,
        expectedCloseDate: editExpectedCloseDate,
        probability: editProbability,
      });
    } catch (error) {
      console.error('Error updating opportunity:', error);
      setSnackbar({ open: true, message: 'Failed to update opportunity', severity: 'error' });
    } finally {
      setUpdatingFields(false);
    }
  };

  // Handle add note
  const handleAddNote = async () => {
    if (!viewingOpportunity || !newNote.trim()) return;

    try {
      const timestamp = new Date().toLocaleString();
      const noteWithTimestamp = `[${timestamp}] ${user?.firstName || 'User'}: ${newNote}`;
      const updatedNotes = notes ? `${notes}\n\n${noteWithTimestamp}` : noteWithTimestamp;

      await crmService.updateOpportunity(viewingOpportunity._id!, { notes: updatedNotes });
      setNotes(updatedNotes);
      setNewNote('');
      setSnackbar({ open: true, message: 'Note added successfully', severity: 'success' });
      fetchData();
    } catch (error) {
      console.error('Error adding note:', error);
      setSnackbar({ open: true, message: 'Failed to add note', severity: 'error' });
    }
  };

  // Fetch quotations for the selected opportunity
  const fetchOpportunityQuotations = async (opportunityId: string) => {
    try {
      setLoadingQuotations(true);
      const response = await crmService.getQuotations({ opportunityId });
      const quotations = Array.isArray(response) ? response :
        response.quotations ? response.quotations :
        response.data ? response.data : [];
      setOpportunityQuotations(quotations);
    } catch (error) {
      console.error('Error fetching quotations:', error);
      setOpportunityQuotations([]);
    } finally {
      setLoadingQuotations(false);
    }
  };

  // Filter opportunities based on search
  const filteredOpportunities = opportunities.filter(opp => {
    const accountName = typeof opp.account === 'object' && opp.account !== null
      ? opp.account.name
      : opp.accountName || '';
    return `${opp.name} ${accountName} ${opp.stage}`.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Get stage color
  const getStageColor = (stage: string) => {
    const colors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      'Prospect': 'info',
      'First Call': 'primary',
      'Demo': 'secondary',
      'POC': 'warning',
      'RFI/RFQ': 'warning',
      'Negotiation': 'warning',
      'Closed Won': 'success',
      'Closed Lost': 'error'
    };
    return colors[stage] || 'default';
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculate pipeline value
  const pipelineValue = opportunities
    .filter(opp => opp.stage !== 'Closed Lost' && opp.stage !== 'Closed Won')
    .reduce((sum, opp) => sum + (opp.amount * opp.probability / 100), 0);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUp sx={{ color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">{opportunities.length}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Opportunities</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AttachMoney sx={{ color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">{formatCurrency(pipelineValue)}</Typography>
                  <Typography variant="body2" color="text.secondary">Pipeline Value</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmojiEvents sx={{ color: 'warning.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">
                    {opportunities.filter(o => o.stage === 'Closed Won').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Won Deals</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarToday sx={{ color: 'info.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">
                    {opportunities.filter(o => {
                      const closeDate = new Date(o.expectedCloseDate);
                      const today = new Date();
                      const thirtyDays = new Date(today.setDate(today.getDate() + 30));
                      return closeDate <= thirtyDays && o.stage !== 'Closed Won' && o.stage !== 'Closed Lost';
                    }).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Closing in 30 Days</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 2, flex: 1, mr: 2 }}>
            <TextField
              placeholder="Search opportunities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, maxWidth: 400 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchData}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Opportunities Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Opportunity Name</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Stage</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Probability</TableCell>
                  <TableCell>Expected Close</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={8}>
                        <Skeleton variant="text" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : filteredOpportunities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        No opportunities found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOpportunities
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((opportunity) => (
                      <TableRow
                        key={opportunity._id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                        onClick={() => handleViewDetails(opportunity)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {opportunity.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Business fontSize="small" color="action" />
                            {typeof opportunity.account === 'object' && opportunity.account !== null
                              ? opportunity.account.name
                              : opportunity.accountName || opportunity.account || 'N/A'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={opportunity.stage}
                            color={getStageColor(opportunity.stage)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="medium">
                            {formatCurrency(opportunity.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={opportunity.probability}
                              sx={{ width: 50, height: 6, borderRadius: 3 }}
                            />
                            <Typography variant="body2">{opportunity.probability}%</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {new Date(opportunity.expectedCloseDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{opportunity.type}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewDetails(opportunity);
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredOpportunities.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>

        {/* Create/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingOpportunity ? 'Edit Opportunity' : 'Create New Opportunity'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Opportunity Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Account"
                  name="account"
                  value={formData.account}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="">Select Account</MenuItem>
                  {accounts.map((account) => (
                    <MenuItem key={account._id} value={account._id}>
                      {account.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Type"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                >
                  <MenuItem value="New Business">New Business</MenuItem>
                  <MenuItem value="Existing Business">Existing Business</MenuItem>
                  <MenuItem value="Renewal">Renewal</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Stage"
                  name="stage"
                  value={formData.stage}
                  onChange={(e) => handleStageChange(e.target.value)}
                >
                  <MenuItem value="Prospect">Prospect</MenuItem>
                  <MenuItem value="First Call">First Call</MenuItem>
                  <MenuItem value="Demo">Demo</MenuItem>
                  <MenuItem value="POC">POC</MenuItem>
                  <MenuItem value="RFI/RFQ">RFI/RFQ</MenuItem>
                  <MenuItem value="Negotiation">Negotiation</MenuItem>
                  <MenuItem value="Closed Won">Closed Won</MenuItem>
                  <MenuItem value="Closed Lost">Closed Lost</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Probability (%)"
                  name="probability"
                  type="number"
                  value={formData.probability}
                  onChange={handleChange}
                  InputProps={{
                    inputProps: { min: 0, max: 100 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Amount"
                  name="amount"
                  type="number"
                  value={formData.amount}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Expected Close Date"
                  name="expectedCloseDate"
                  type="date"
                  value={formData.expectedCloseDate}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Next Step"
                  name="nextStep"
                  value={formData.nextStep}
                  onChange={handleChange}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              onClick={handleSave} 
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #63739c 0%, #aeb6bf 100%)',
              }}
            >
              {editingOpportunity ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Opportunity Detail Dialog */}
        <Dialog
          open={openDetailDialog}
          onClose={handleCloseDetailDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">{viewingOpportunity?.name}</Typography>
              <IconButton onClick={handleCloseDetailDialog} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Tabs
              value={detailTab}
              onChange={(_, newValue) => {
                setDetailTab(newValue);
                if (newValue === 2 && viewingOpportunity?._id && opportunityQuotations.length === 0) {
                  fetchOpportunityQuotations(viewingOpportunity._id);
                }
              }}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
            >
              <Tab label="Details" />
              <Tab label="Notes" icon={<NotesIcon />} iconPosition="start" />
              <Tab label="Quotations" icon={<RequestQuote />} iconPosition="start" />
            </Tabs>

            {/* Details Tab */}
            {detailTab === 0 && viewingOpportunity && (
              <Box>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Update Opportunity
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <FormControl fullWidth>
                              <InputLabel>Stage</InputLabel>
                              <Select
                                value={viewingOpportunity.stage}
                                label="Stage"
                                onChange={(e) => handleStageUpdate(e.target.value)}
                                disabled={updatingStage}
                              >
                                <MenuItem value="Prospect">Prospect</MenuItem>
                                <MenuItem value="First Call">First Call</MenuItem>
                                <MenuItem value="Demo">Demo</MenuItem>
                                <MenuItem value="POC">POC</MenuItem>
                                <MenuItem value="RFI/RFQ">RFI/RFQ</MenuItem>
                                <MenuItem value="Negotiation">Negotiation</MenuItem>
                                <MenuItem value="Closed Won">Closed Won</MenuItem>
                                <MenuItem value="Closed Lost">Closed Lost</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Expected Close Date"
                              type="date"
                              value={editExpectedCloseDate}
                              onChange={(e) => setEditExpectedCloseDate(e.target.value)}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Probability (%)"
                              type="number"
                              value={editProbability}
                              onChange={(e) => setEditProbability(Number(e.target.value))}
                              InputProps={{
                                inputProps: { min: 0, max: 100 },
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              variant="contained"
                              startIcon={<Save />}
                              onClick={handleFieldsUpdate}
                              disabled={updatingFields}
                              sx={{
                                background: 'linear-gradient(135deg, #63739c 0%, #aeb6bf 100%)',
                              }}
                            >
                              {updatingFields ? 'Saving...' : 'Save Changes'}
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Account
                    </Typography>
                    <Typography variant="body1">
                      {typeof viewingOpportunity.account === 'object' && viewingOpportunity.account !== null
                        ? viewingOpportunity.account.name
                        : viewingOpportunity.accountName || 'N/A'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Amount
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatCurrency(viewingOpportunity.amount)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Type
                    </Typography>
                    <Typography variant="body1">{viewingOpportunity.type}</Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Current Stage
                    </Typography>
                    <Chip
                      label={viewingOpportunity.stage}
                      color={getStageColor(viewingOpportunity.stage)}
                      size="small"
                    />
                  </Grid>

                  {/* Primary Contact Section */}
                  {viewingOpportunity.primaryContact && typeof viewingOpportunity.primaryContact === 'object' && (
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ mt: 1 }}>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Primary Contact
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">Name</Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {(viewingOpportunity.primaryContact as Contact).firstName} {(viewingOpportunity.primaryContact as Contact).lastName}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">Title</Typography>
                              <Typography variant="body1">
                                {(viewingOpportunity.primaryContact as Contact).title || '-'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">Email</Typography>
                              <Typography variant="body1">
                                {(viewingOpportunity.primaryContact as Contact).email || '-'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">Phone</Typography>
                              <Typography variant="body1">
                                {(viewingOpportunity.primaryContact as Contact).phone || (viewingOpportunity.primaryContact as Contact).mobile || '-'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {viewingOpportunity.description && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Description
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {viewingOpportunity.description}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}

                  {viewingOpportunity.nextStep && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Next Steps
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'warning.lighter' }}>
                        <Typography variant="body2">
                          {viewingOpportunity.nextStep}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {/* Notes Tab */}
            {detailTab === 1 && (
              <Box>
                <Stack spacing={3}>
                  {/* Add New Note */}
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Add Note
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Write your note here..."
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        sx={{ mb: 2 }}
                      />
                      <Button
                        variant="contained"
                        startIcon={<Save />}
                        onClick={handleAddNote}
                        disabled={!newNote.trim()}
                        sx={{
                          background: 'linear-gradient(135deg, #63739c 0%, #aeb6bf 100%)',
                        }}
                      >
                        Add Note
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Existing Notes */}
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Notes History
                    </Typography>
                    {notes ? (
                      <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                          }}
                        >
                          {notes}
                        </Typography>
                      </Paper>
                    ) : (
                      <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                        <NotesIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                        <Typography color="text.secondary">
                          No notes yet. Add your first note above.
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                </Stack>
              </Box>
            )}

            {/* Quotations Tab */}
            {detailTab === 2 && (
              <Box>
                {loadingQuotations ? (
                  <Box>
                    {[...Array(3)].map((_, i) => (
                      <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
                    ))}
                  </Box>
                ) : opportunityQuotations.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                    <RequestQuote sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">
                      No quotations found for this opportunity.
                    </Typography>
                  </Paper>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Quotation #</TableCell>
                          <TableCell>Account</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell>Valid Until</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {opportunityQuotations.map((quotation) => {
                          const isExpired = quotation.validUntil && new Date(quotation.validUntil) < new Date();
                          const statusColor: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
                            'Draft': 'default',
                            'Sent': 'info',
                            'Viewed': 'primary',
                            'Accepted': 'success',
                            'Rejected': 'error',
                            'Expired': 'warning',
                            'Converted': 'success',
                            'Cancelled': 'error',
                          };
                          return (
                            <TableRow key={quotation._id} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {quotation.quotationNumber}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {typeof quotation.account === 'object' && quotation.account !== null
                                  ? (quotation.account as Account).name
                                  : quotation.accountName || '-'}
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight="medium">
                                  {formatCurrency(quotation.totalAmount)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  color={isExpired ? 'error.main' : 'text.primary'}
                                >
                                  {quotation.validUntil
                                    ? new Date(quotation.validUntil).toLocaleDateString()
                                    : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={quotation.status}
                                  color={statusColor[quotation.status] || 'default'}
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </DialogContent>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </motion.div>
    </Container>
  );
};

export default OpportunitiesPage;